import React, { useState, useEffect } from 'react';
import { Spinner, Modal, Form, Button } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import {
  fetchOrganizationDetails,
  suspendOrganization,
  fetchSubscriptionPlans,
  assignPlanToOrg
} from '../../../actioncreators/actioncreators';

/* ── Styles ── */
const s = {
  page: { minHeight: '100vh', backgroundColor: '#f1f5f9', padding: '24px' },
  backBtn: {
    display: 'inline-flex', alignItems: 'center', gap: '6px',
    border: 'none', background: 'none', color: '#64748b', fontSize: '13px',
    cursor: 'pointer', padding: '4px 0', marginBottom: '16px', fontWeight: 500,
  },
  titleRow: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' },
  pageTitle: { fontSize: '22px', fontWeight: 700, color: '#0f172a', margin: '0 0 4px 0' },
  badge: (suspended, status) => ({
    display: 'inline-flex', alignItems: 'center', gap: '5px',
    padding: '4px 12px', borderRadius: '999px', fontSize: '12px', fontWeight: 600,
    backgroundColor: suspended ? '#fee2e2' : status === 'ACTIVE' ? '#dcfce7' : '#f1f5f9',
    color: suspended ? '#dc2626' : status === 'ACTIVE' ? '#16a34a' : '#64748b',
  }),
  actionRow: { display: 'flex', gap: '10px', flexWrap: 'wrap' },
  card: { backgroundColor: '#fff', borderRadius: '16px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', marginBottom: '20px', overflow: 'hidden' },
  sectionHeader: { display: 'flex', alignItems: 'center', gap: '10px', padding: '16px 24px', borderBottom: '1px solid #f1f5f9' },
  iconBox: (bg) => ({ width: '32px', height: '32px', borderRadius: '8px', backgroundColor: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '15px', flexShrink: 0 }),
  sectionTitle: { fontSize: '13px', fontWeight: 700, color: '#0f172a', margin: 0 },
  sectionSub: { fontSize: '11px', color: '#94a3b8', margin: 0 },
  grid: (cols) => ({ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: '0', padding: '0' }),
  infoItem: { padding: '16px 24px', borderBottom: '1px solid #f8fafc', borderRight: '1px solid #f8fafc' },
  infoLabel: { fontSize: '11px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px', display: 'block' },
  infoValue: { fontSize: '13px', color: '#0f172a', fontWeight: 500, wordBreak: 'break-word' },
  infoValueMono: { fontSize: '12px', color: '#6366f1', fontWeight: 600, fontFamily: 'monospace', wordBreak: 'break-all' },
  btnPrimary: { display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '9px 18px', borderRadius: '8px', border: 'none', fontSize: '13px', fontWeight: 600, cursor: 'pointer', backgroundColor: '#6366f1', color: '#fff' },
  btnDanger: { display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '9px 18px', borderRadius: '8px', border: 'none', fontSize: '13px', fontWeight: 600, cursor: 'pointer', backgroundColor: '#fee2e2', color: '#dc2626' },
  btnSuccess: { display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '9px 18px', borderRadius: '8px', border: 'none', fontSize: '13px', fontWeight: 600, cursor: 'pointer', backgroundColor: '#dcfce7', color: '#16a34a' },
  btnGhost: { display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '9px 18px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '13px', fontWeight: 600, cursor: 'pointer', backgroundColor: '#fff', color: '#475569' },
  statCard: { backgroundColor: '#fff', borderRadius: '12px', padding: '20px', textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' },
  statNum: { fontSize: '28px', fontWeight: 800, color: '#0f172a', margin: '0 0 4px 0' },
  statLabel: { fontSize: '12px', color: '#64748b', margin: 0 },
};

const InfoItem = ({ label, value, mono }) => (
  <div style={s.infoItem}>
    <span style={s.infoLabel}>{label}</span>
    <span style={mono ? s.infoValueMono : s.infoValue}>{value || 'N/A'}</span>
  </div>
);

const SectionCard = ({ icon, bg, title, subtitle, children }) => (
  <div style={s.card}>
    <div style={s.sectionHeader}>
      <div style={s.iconBox(bg)}>{icon}</div>
      <div>
        <p style={s.sectionTitle}>{title}</p>
        {subtitle && <p style={s.sectionSub}>{subtitle}</p>}
      </div>
    </div>
    {children}
  </div>
);

const OrganizationDetails = () => {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState('');
  const [maxUsers, setMaxUsers] = useState('10');
  const [planStartDate, setPlanStartDate] = useState('');
  const [planEndDate, setPlanEndDate] = useState('');
  const [planDateError, setPlanDateError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (orgId) { fetchDetails(); loadPlans(); }
  }, [orgId]);

  const fetchDetails = async () => {
    setLoading(true); setError(null);
    try {
      const res = await fetchOrganizationDetails(orgId);
      setOrg(res.data?.data || res?.data || null);
    } catch (err) {
      setError(err.message || 'Failed to load organization details');
    } finally {
      setLoading(false);
    }
  };

  const loadPlans = async () => {
    try {
      const res = await fetchSubscriptionPlans();
      setPlans(res.data?.data || res?.data || []);
    } catch {}
  };

  const handleSuspend = async () => {
    setActionLoading(true);
    try {
      await suspendOrganization(orgId, org?.is_suspended ? 'ACTIVATE' : 'SUSPEND', suspendReason);
      setShowSuspendModal(false); setSuspendReason(''); fetchDetails();
    } catch {} finally { setActionLoading(false); }
  };

  const handleChangePlan = async () => {
    if (!selectedPlan) return;
    if (!planStartDate) {
      setPlanDateError('Start date is required.');
      return;
    }
    if (!planEndDate) {
      setPlanDateError('End date is required.');
      return;
    }
    if (planEndDate < planStartDate) {
      setPlanDateError('End date must be greater than or equal to start date.');
      return;
    }
    setPlanDateError('');
    setActionLoading(true);
    try {
      const startDateTime = new Date(planStartDate + 'T00:00:00').toISOString();
      const endDateTime = new Date(planEndDate + 'T23:59:59').toISOString();
      await assignPlanToOrg({ org_id: orgId, package_id: selectedPlan, max_users: parseInt(maxUsers, 10) || 1, start_date: startDateTime, end_date: endDateTime });
      setShowPlanModal(false);
      setPlanStartDate(''); setPlanEndDate(''); setPlanDateError('');
      fetchDetails();
    } catch {} finally { setActionLoading(false); }
  };

  const fmt = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' }) : 'N/A';

  const address = [org?.address_line_1, org?.address_line_2, org?.city, org?.district, org?.state, org?.pincode, org?.country]
    .filter(Boolean).join(', ');

  if (loading) return (
    <div style={{ ...s.page, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Spinner animation="border" /> <span style={{ marginLeft: '12px', color: '#64748b' }}>Loading...</span>
    </div>
  );

  if (error) return (
    <div style={{ ...s.page, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
      <p style={{ color: '#dc2626', marginBottom: '16px' }}>Error: {error}</p>
      <button style={s.btnPrimary} onClick={() => navigate('/home/super-admin/organizations')}>Back to Organizations</button>
    </div>
  );

  if (!org) return (
    <div style={{ ...s.page, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
      <p style={{ color: '#64748b', marginBottom: '16px' }}>Organization not found</p>
      <button style={s.btnPrimary} onClick={() => navigate('/home/super-admin/organizations')}>Back to Organizations</button>
    </div>
  );

  const isSuspended = org.is_suspended;
  const isActive = org.status === 'ACTIVE';

  return (
    <div style={s.page}>

      {/* Back */}
      <button style={s.backBtn} onClick={() => navigate('/home/super-admin/organizations')}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><path d="M19 12H5M12 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        Back to Organizations
      </button>

      {/* Title row */}
      <div style={s.titleRow}>
        <div>
          <h1 style={s.pageTitle}>{org.org_name}</h1>
          <span style={s.badge(isSuspended, org.status)}>
            ● {isSuspended ? 'Suspended' : isActive ? 'Active' : 'Inactive'}
          </span>
        </div>
        <div style={s.actionRow}>
          <button style={isSuspended ? s.btnSuccess : s.btnDanger} onClick={() => setShowSuspendModal(true)}>
            {isSuspended ? '✓ Activate Org' : '⊘ Suspend Org'}
          </button>
          <button style={s.btnPrimary} onClick={() => setShowPlanModal(true)}>
            ✎ Change Plan
          </button>
          <button style={s.btnGhost} onClick={() => navigate(`/home/super-admin/audit-logs?org_id=${orgId}`)}>
            ☰ View Audit
          </button>
        </div>
      </div>

      {/* Stats row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '20px' }}>
        {[
          { num: org.user_count ?? 0, label: 'Active Users' },
          { num: org.location_count ?? 0, label: 'Locations' },
          { num: org.child_org_count ?? 0, label: 'Child Orgs' },
          { num: org.total_descendant_count ?? 0, label: 'Total Descendants' },
        ].map(({ num, label }) => (
          <div key={label} style={s.statCard}>
            <p style={s.statNum}>{num}</p>
            <p style={s.statLabel}>{label}</p>
          </div>
        ))}
      </div>

      {/* Basic Info */}
      <SectionCard icon="🏢" bg="#dbeafe" title="Organization Information" subtitle="Core details about the organization">
        <div style={s.grid(3)}>
          <InfoItem label="Org ID" value={org.org_id} mono />
          <InfoItem label="Organization Name" value={org.org_name} />
          <InfoItem label="Status" value={isSuspended ? 'Suspended' : org.status} />
          <InfoItem label="Email" value={org.email} />
          <InfoItem label="Mobile" value={org.mobile} />
          <InfoItem label="Org Type" value={org.org_type} />
          <InfoItem label="Org Size" value={org.org_size} />
          <InfoItem label="GST Number" value={org.gst_number} />
          <InfoItem label="Hierarchy Level" value={org.hierarchy_level} />
          <InfoItem label="Org Path" value={org.org_path} mono />
          <InfoItem label="Created On" value={fmt(org.creation_date_and_time)} />
          <InfoItem label="Last Updated" value={fmt(org.updated_date_and_time)} />
        </div>
      </SectionCard>

      {/* Address */}
      <SectionCard icon="📍" bg="#dcfce7" title="Address" subtitle="Registered office location">
        <div style={s.grid(3)}>
          <InfoItem label="Address Line 1" value={org.address_line_1} />
          <InfoItem label="Address Line 2" value={org.address_line_2} />
          <InfoItem label="City" value={org.city} />
          <InfoItem label="District" value={org.district} />
          <InfoItem label="State" value={org.state} />
          <InfoItem label="Pincode" value={org.pincode} />
          <InfoItem label="Country" value={org.country} />
          <InfoItem label="Full Address" value={address} />
        </div>
      </SectionCard>

      {/* Subscription */}
      <SectionCard icon="💳" bg="#fef9c3" title="Subscription" subtitle="Current plan and billing details">
        <div style={s.grid(3)}>
          <InfoItem label="Plan Name" value={org.package_name || org.plan} />
          <InfoItem label="Plan Type" value={org.package_type} />
          <InfoItem label="Subscription Status" value={org.subscription_status} />
          <InfoItem label="Max Users" value={org.max_users} />
          <InfoItem label="Start Date" value={fmt(org.subscription_start_date)} />
          <InfoItem label="End Date" value={fmt(org.subscription_end_date)} />
        </div>
      </SectionCard>

      {/* Hierarchy */}
      <SectionCard icon="🔗" bg="#ede9fe" title="Hierarchy" subtitle="Parent and root organization">
        <div style={s.grid(3)}>
          <InfoItem label="Parent Org" value={org.parent_org_name} />
          <InfoItem label="Parent Org ID" value={org.parent_org_id} mono />
          <InfoItem label="Root Org" value={org.root_org_name} />
          <InfoItem label="Root Org ID" value={org.root_org_id} mono />
        </div>
      </SectionCard>

      {/* Admin */}
      <SectionCard icon="👤" bg="#fce7f3" title="Admin Contact" subtitle="Assigned administrator">
        <div style={s.grid(3)}>
          <InfoItem label="Admin Name" value={org.admin_name} />
          <InfoItem label="Admin Email" value={org.admin_email} />
          <InfoItem label="Privacy Policy URL" value={org.privacy_policy_url} />
        </div>
      </SectionCard>

      {/* Suspension info — only if suspended */}
      {isSuspended && (
        <SectionCard icon="⚠️" bg="#fee2e2" title="Suspension Details" subtitle="Why this org was suspended">
          <div style={s.grid(3)}>
            <InfoItem label="Suspension Reason" value={org.suspension_reason} />
            <InfoItem label="Suspended At" value={fmt(org.suspended_at)} />
            <InfoItem label="Suspended By" value={org.suspended_by} mono />
          </div>
        </SectionCard>
      )}

      {/* Suspend/Activate Modal */}
      <Modal show={showSuspendModal} onHide={() => setShowSuspendModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '15px', fontWeight: 700 }}>
            {isSuspended ? 'Activate' : 'Suspend'} Organization
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ fontSize: '13px', color: '#475569' }}>
            Are you sure you want to {isSuspended ? 'activate' : 'suspend'} <strong>{org.org_name}</strong>?
          </p>
          {!isSuspended && (
            <Form.Group>
              <Form.Label style={{ fontSize: '12px', fontWeight: 600 }}>Reason for Suspension</Form.Label>
              <Form.Control as="textarea" rows={3} value={suspendReason} onChange={e => setSuspendReason(e.target.value)} placeholder="Enter reason..." style={{ fontSize: '13px' }} />
            </Form.Group>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" size="sm" onClick={() => setShowSuspendModal(false)}>Cancel</Button>
          <Button variant={isSuspended ? 'success' : 'danger'} size="sm" onClick={handleSuspend} disabled={actionLoading}>
            {actionLoading ? <Spinner size="sm" /> : isSuspended ? 'Activate' : 'Suspend'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Change Plan Modal */}
      <Modal show={showPlanModal} onHide={() => setShowPlanModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '15px', fontWeight: 700 }}>Change Subscription Plan</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label style={{ fontSize: '12px', fontWeight: 600 }}>Select Plan</Form.Label>
            <Form.Select value={selectedPlan} onChange={e => setSelectedPlan(e.target.value)} style={{ fontSize: '13px' }}>
              <option value="">Select a plan...</option>
              {plans.filter(p => p.status === 'ACTIVE').map(p => <option key={p.package_id} value={p.package_id}>{p.package_name} — {p.package_type}</option>)}
            </Form.Select>
          </Form.Group>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
            <Form.Group>
              <Form.Label style={{ fontSize: '12px', fontWeight: 600 }}>Start Date *</Form.Label>
              <Form.Control
                type="date"
                value={planStartDate}
                required
                onChange={e => { setPlanStartDate(e.target.value); setPlanDateError(''); }}
                style={{ fontSize: '13px', borderColor: (planDateError && !planStartDate) ? '#dc2626' : undefined }}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label style={{ fontSize: '12px', fontWeight: 600 }}>End Date *</Form.Label>
              <Form.Control
                type="date"
                value={planEndDate}
                min={planStartDate || undefined}
                required
                onChange={e => { setPlanEndDate(e.target.value); setPlanDateError(''); }}
                style={{ fontSize: '13px', borderColor: (planDateError && (!planEndDate || planEndDate < planStartDate)) ? '#dc2626' : undefined }}
              />
              {planDateError && (
                <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>{planDateError}</div>
              )}
            </Form.Group>
          </div>
          <Form.Group>
            <Form.Label style={{ fontSize: '12px', fontWeight: 600 }}>Max Users</Form.Label>
            <Form.Control
              type="number"
              value={maxUsers}
              onChange={e => setMaxUsers(e.target.value.replace(/^0+(?=\d)/, ''))}
              onFocus={e => e.target.select()}
              onBlur={() => {
                const v = parseInt(maxUsers, 10);
                setMaxUsers(isNaN(v) || v < 1 ? '1' : String(v));
              }}
              min={1}
              style={{ fontSize: '13px' }}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" size="sm" onClick={() => setShowPlanModal(false)}>Cancel</Button>
          <Button variant="primary" size="sm" onClick={handleChangePlan} disabled={!selectedPlan || actionLoading}>
            {actionLoading ? <Spinner size="sm" /> : 'Update Plan'}
          </Button>
        </Modal.Footer>
      </Modal>

    </div>
  );
};

export default OrganizationDetails;
